import React from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Briefcase, Users2, Wallet, Send } from "lucide-react";
import { useLocalization } from "../../hooks/useLocalization";
import { SimpleMarkdown } from "./SimpleMarkdown";
import { API } from "../../lib/api";

/** Full job posting detail with a prominent "Apply Now" button that leads to the dedicated,
 * single-page application form (/careers/jobs/:jobId/apply) - shared by the Careers listing
 * (rendered directly when there is a single open job, so applicants never have to drill into
 * a separate detail page) and the bookmarkable /careers/jobs/:jobId route. */
export const JobPostingDetail = ({ job, headingLevel = "h1" }) => {
  const navigate = useNavigate();
  const { t, language } = useLocalization();

  const title = (language === "ja" ? job.title_ja : job.title_en) || job.title_en;
  const description = (language === "ja" ? job.description_ja : job.description_en) || job.description_en;
  const requirements = (language === "ja" ? job.requirements_ja : job.requirements_en) || job.requirements_en;
  const heroAlt = (language === "ja" ? job.hero_image_alt_ja : job.hero_image_alt_en) || job.hero_image_alt_en || title;
  const Heading = headingLevel;
  const goToApply = () => navigate(`/careers/jobs/${job.id}/apply`);

  return (
    <div data-testid={`job-posting-detail-${job.id}`} className="max-w-3xl">
      {job.hero_image_available && (
        <a href={`${API}/careers/jobs/${job.id}/hero-image?size=large`} target="_blank" rel="noreferrer" data-testid="job-hero-image-link" className="block mb-6 rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
          <img
            data-testid="job-hero-image"
            src={`${API}/careers/jobs/${job.id}/hero-image?size=medium`}
            srcSet={`${API}/careers/jobs/${job.id}/hero-image?size=small 480w, ${API}/careers/jobs/${job.id}/hero-image?size=medium 960w, ${API}/careers/jobs/${job.id}/hero-image?size=large 1600w`}
            sizes="(max-width: 640px) 100vw, 768px"
            alt={heroAlt}
            loading="lazy"
            className="w-full h-auto"
          />
        </a>
      )}
      <div className="flex flex-wrap gap-2 mb-4">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400 text-blue-950 text-xs font-bold uppercase">
          <Briefcase size={13} /> {job.employment_type?.replaceAll("_", " ")}
        </span>
        <span data-testid="job-multiple-candidates-badge" className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold uppercase">
          <Users2 size={13} /> {t("careers.multipleCandidatesWelcome")}
        </span>
      </div>
      <Heading data-testid="career-job-title" className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight mb-3 break-words text-blue-950">{title}</Heading>
      <p data-testid="career-job-location" className="flex items-start gap-1.5 text-gray-500 text-sm mb-5 break-words">
        <MapPin size={14} className="shrink-0 mt-0.5" /> <span>{t("careers.locationYokosuka")}</span>
      </p>

      {/* Job summary strip - the key facts, scannable before reading the full posting. */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="job-summary-strip">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200 text-gray-700 text-xs font-semibold">
          <Wallet size={13} className="text-amber-500" /> {t("careers.trialWage")}
        </span>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200 text-gray-700 text-xs font-semibold">
          <Wallet size={13} className="text-amber-500" /> {t("careers.postTrialWage")}
        </span>
        {job.has_shift_premium && (
          <span data-testid="job-shift-premium-badge" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-bold">
            <Wallet size={13} className="text-red-500" /> {t("careers.shiftPremiumBadge")}
          </span>
        )}
      </div>

      <button
        type="button"
        data-testid="job-summary-apply-cta"
        onClick={goToApply}
        className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-blue-950 text-white font-bold hover:bg-amber-400 hover:text-blue-950 active:scale-95 transition-all shadow-lg shadow-blue-950/10 mb-8"
      >
        <Send size={16} /> {t("careers.applyNowCta")}
      </button>

      <SimpleMarkdown text={description} testId="career-job-description" />

      {job.has_shift_premium && (
        <div data-testid="job-shift-premium-section" className="bg-red-50 rounded-2xl border border-red-200 p-4 sm:p-5 mt-6 mb-6">
          <p className="text-xs font-bold uppercase tracking-wide text-red-700 mb-2">{t("careers.premiumSectionTitle")}</p>
          <p className="text-sm text-gray-700 mb-3">{t("careers.premiumSectionBody")}</p>
          <ul className="list-disc pl-5 text-sm text-gray-800 space-y-1 mb-3">
            <li data-testid="job-shift-premium-opening">{t("careers.premiumShiftOpening")}</li>
            <li data-testid="job-shift-premium-closing">{t("careers.premiumShiftClosing")}</li>
          </ul>
          <p className="text-xs text-gray-500">{t("careers.premiumSectionFooter")}</p>
        </div>
      )}

      {requirements && (
        <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4 sm:p-5 mt-6 mb-6">
          <p className="text-xs font-bold uppercase tracking-wide text-amber-600 mb-2">{t("careers.requirements")}</p>
          <SimpleMarkdown text={requirements} testId="career-job-requirements" />
        </div>
      )}

      <button
        type="button"
        data-testid="job-bottom-apply-cta"
        onClick={goToApply}
        className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-blue-950 text-white font-bold hover:bg-amber-400 hover:text-blue-950 active:scale-95 transition-all shadow-lg shadow-blue-950/10"
      >
        <Send size={16} /> {t("careers.applyNowCta")}
      </button>
    </div>
  );
};
